import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiCheck, FiChevronDown, FiChevronLeft, FiChevronRight, FiFilter, FiPackage, FiSearch, FiShoppingBag, FiTruck } from "react-icons/fi";
import { useLocation, useSearchParams } from "react-router-dom";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { flagSplitBuyPurchased, getCachedOpsOrders, getOpsOrders } from "../../../../../api/adminOpsOrders";
import { ComponentLoader } from "../../../../../components/loaders";
import { DashboardHeader, MetricsGrid } from "../../dashboards/dashboard-components";
import { readAdminPageState, saveAdminPageState } from "../../../../../utils/adminPageState";
import { OpsOrdersModalGlobalStyle } from "../ops-orders.style";
import { activateNotification } from "../../../../../utils/notifications-utils";
import { OrderAccordion } from "./order-accordion.section";
import { formatOpsCurrency, formatOpsDate, getPaginationItems } from "../ops-orders.utils";

export const OpsOrdersSection = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialPageState] = useState(() => readAdminPageState(location.pathname, {
    query: searchParams.get("query") || "",
    filter: "all",
    period: "all",
    startDate: "",
    endDate: "",
    deliveryDate: searchParams.get("delivery_date") || "",
    campaignStatus: "active",
  }, { query: "", filter: "all", period: "all", startDate: "", endDate: "", deliveryDate: "", campaignStatus: "active" }));
  const filterMenuRef = useRef(null);
  const latestRequestRef = useRef(0);
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({
    total_items: 0,
    total_orders_count: 0,
    total_purchased_orders: 0,
    total_fulfilled_orders: 0,
    current_page: 1,
    total_pages: 1,
    pageSize: 10,
  });
  const [query, setQuery] = useState(initialPageState.query);
  const [debouncedQuery, setDebouncedQuery] = useState(initialPageState.query);
  const [filter, setFilter] = useState(initialPageState.filter);
  const [period, setPeriod] = useState(initialPageState.period);
  const [startDate, setStartDate] = useState(initialPageState.startDate);
  const [endDate, setEndDate] = useState(initialPageState.endDate);
  const [deliveryDate, setDeliveryDate] = useState(initialPageState.deliveryDate);
  const [campaignStatus, setCampaignStatus] = useState(initialPageState.campaignStatus);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyCampaign, setBusyCampaign] = useState(null);
  const [purchaseConfirmation, setPurchaseConfirmation] = useState(null);

  const loadOrders = useCallback(async (page = 1) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const options = {
      page,
      pageSize: pagination.pageSize,
      period: period === "all" ? undefined : period,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      delivery_date: deliveryDate || undefined,
      status: campaignStatus === "all" ? undefined : campaignStatus,
      purchased_flag: filter === "all" ? undefined : filter === "purchased",
      search: debouncedQuery.trim() || undefined,
    };
    const cached = getCachedOpsOrders(options);
    if (cached) {
      setCampaigns(cached.data);
      setPagination(cached.pagination);
    }
    setLoading(!cached);
    setLoadError("");
    try {
      const response = await getOpsOrders(options);
      if (requestId !== latestRequestRef.current) return;
      if (!response.status) throw new Error(response.message || "Orders could not be loaded.");
      setCampaigns(response.data);
      setPagination(response.pagination);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      setCampaigns([]);
      activateNotification({ title: "Error", body: error.response?.data?.message || error.message || "Orders could not be loaded."});
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [campaignStatus, debouncedQuery, deliveryDate, endDate, filter, pagination.pageSize, period, startDate]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    saveAdminPageState(location.pathname, { query, filter, period, startDate, endDate, deliveryDate, campaignStatus });
  }, [campaignStatus, deliveryDate, endDate, filter, location.pathname, period, query, startDate]);

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (query) next.set("query", query);
      else next.delete("query");
      if (deliveryDate) next.set("delivery_date", deliveryDate);
      else next.delete("delivery_date");
      return next;
    }, { replace: true });
  }, [deliveryDate, query, setSearchParams]);

  const changeQuery = (event) => {
    setQuery(event.target.value);
  };

  useEffect(() => {
    loadOrders(1);
  }, [loadOrders]);

  useEffect(() => {
    const closeFilterMenu = (event) => {
      const filterMenu = filterMenuRef.current;
      if (filterMenu?.open && !filterMenu.contains(event.target)) filterMenu.removeAttribute("open");
    };

    document.addEventListener("pointerdown", closeFilterMenu);
    return () => document.removeEventListener("pointerdown", closeFilterMenu);
  }, []);

  const visibleCampaigns = campaigns;
  const paginationItems = useMemo(() => getPaginationItems(pagination.current_page, pagination.total_pages), [pagination.current_page, pagination.total_pages]);
  const rangeStart = pagination.total_items ? ((pagination.current_page - 1) * pagination.pageSize) + 1 : 0;
  const rangeEnd = Math.min(pagination.current_page * pagination.pageSize, pagination.total_items);
  const activeFilterCount = [period !== "all" || Boolean(startDate || endDate), Boolean(deliveryDate), campaignStatus !== "all", filter !== "all"].filter(Boolean).length;
  const resetFilters = () => { setPeriod("all"); setStartDate(""); setEndDate(""); setDeliveryDate(""); setCampaignStatus("all"); setFilter("all"); };
  const changePeriod = (event) => {
    setPeriod(event.target.value);
    setStartDate("");
    setEndDate("");
  };
  const changeStartDate = (event) => {
    setStartDate(event.target.value);
    if (event.target.value) setPeriod("all");
  };
  const changeEndDate = (event) => {
    setEndDate(event.target.value);
    if (event.target.value) setPeriod("all");
  };

  const metrics = useMemo(() => {
    return [
      { label: "Split-Buy campaigns", value: pagination.total_items.toLocaleString(), note: "across all pages", icon: FiPackage, color: "blue" },
      { label: "Customer orders", value: pagination.total_orders_count.toLocaleString(), note: "across all campaigns", icon: FiShoppingBag },
      { label: "Purchased orders", value: pagination.total_purchased_orders.toLocaleString(), note: "supplier purchase complete", icon: FiCheck, color: "amber" },
      { label: "Fulfilled orders", value: pagination.total_fulfilled_orders.toLocaleString(), note: "delivered or completed", icon: FiTruck },
    ];
  }, [pagination.total_fulfilled_orders, pagination.total_items, pagination.total_orders_count, pagination.total_purchased_orders]);

  const markPurchased = async (campaign) => {
    setBusyCampaign(campaign.split_buy_id);
    try {
      const response = await flagSplitBuyPurchased(campaign.split_buy_id);
      setCampaigns((current) => current.map((item) => item.split_buy_id === campaign.split_buy_id ? { ...item, purchased_flag: response.purchased_flag, purchased_at: response.purchased_at, orders: item.orders.map((order) => ({ ...order, fulfillment_status: order.fulfillment_status === "pending" ? "purchased" : order.fulfillment_status })) } : item));
      activateNotification({
        title: "Split-Buy marked as purchased",
        body: response.message || "The campaign and its orders were updated.",
      });
      setPurchaseConfirmation(null);
    } catch (error) {
      activateNotification({title: "Error", body: error.response?.data?.message || error.message || "The Split-Buy could not be marked as purchased."})
    } finally {
      setBusyCampaign(null);
    }
  };

  return (
    <>
      <OpsOrdersModalGlobalStyle />
      <DashboardHeader eyebrow="Operations workspace" title="Order by Split-Buys" description="Review Split-Buy purchasing, customer details, delivery notes and every order’s fulfilment stage." showActions={false} />
      <MetricsGrid metrics={metrics} />
      <div className="page-tools">
        <label className="search-box"><FiSearch aria-hidden="true" /><input value={query} onChange={changeQuery} placeholder="Search product, Split-Buy ID, customer or email" aria-label="Search operations orders" /></label>
        <details className="orders-filter-menu" ref={filterMenuRef}>
          <summary><FiFilter aria-hidden="true" /><span>Filters</span>{activeFilterCount > 0 && <strong>{activeFilterCount}</strong>}<FiChevronDown className="filter-chevron" aria-hidden="true" /></summary>
          <div className="orders-filter-panel">
            <header><div><strong>Filter orders</strong><span>Narrow the fetched campaigns.</span></div><button type="button" onClick={resetFilters} disabled={!activeFilterCount}>Clear all</button></header>
            <div className="filter-grid">
              <label className="filter-full"><span>Status</span><select value={campaignStatus} onChange={(event) => setCampaignStatus(event.target.value)}><option value="all">All statuses</option><option value="completed">Completed</option><option value="active">Active</option></select></label>
              <label className="purchase-filter"><span>Purchase flag</span><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">All purchase flags</option><option value="purchased">Purchased</option><option value="pending">Pending purchase</option></select></label>
              <label className="filter-full"><span>Delivery date</span><input type="date" value={deliveryDate} onChange={(event) => setDeliveryDate(event.target.value)} /></label>
              <label className="filter-full"><span>Period</span><select value={period} onChange={changePeriod}><option value="all">All time</option><option value="7days">Last 7 days</option><option value="14days">Last 14 days</option><option value="30days">Last 30 days</option><option value="biweekly">Biweekly</option></select></label>
              <div className="date-or-divider" role="separator"><span>OR</span></div>
              <label><span>Start date</span><input type="date" value={startDate} max={endDate || undefined} onChange={changeStartDate} /></label>
              <label><span>End date</span><input type="date" value={endDate} min={startDate || undefined} onChange={changeEndDate} /></label>
            </div>
          </div>
        </details>
      </div>

      {loading ? <ComponentLoader /> : loadError ? <section className="content-card"><div className="card-heading"><div><h2>Orders could not be loaded</h2><p>{loadError}</p></div><button type="button" className="action-button" onClick={() => loadOrders(pagination.current_page)}>Try again</button></div></section> : visibleCampaigns.length === 0 ? <section className="content-card"><div className="card-heading"><div><h2>No matching orders</h2><p>Try changing the search, delivery date, status, or purchase filters.</p></div></div></section> : visibleCampaigns.map((campaign) => (
        <details className="campaign" key={campaign.split_buy_id}>
          <summary>
            <div className="product-cell"><div className="product-image"><img src={campaign.product_picture} alt="" /></div><div className="deal-name">{campaign.product_title}<span className="deal-id">Join period end date {formatOpsDate(campaign.join_period_end_date)}</span></div></div>
            <div className="campaign-metrics" aria-label={`${campaign.product_title} campaign summary`}>
              <div className="campaign-stat"><span>Slots</span><strong>{campaign.fractional_number_of_buy_ins_attained} / {campaign.number_of_buy_ins_required}</strong></div>
              <div className="campaign-stat"><span>Orders</span><strong>{campaign.total_orders}</strong></div>
              <div className="campaign-stat"><span>Purchase total</span><strong>{formatOpsCurrency(campaign.total_cost_to_be_split)}</strong></div>
            </div>
            <span className={`status-pill ${campaign.purchased_flag ? "success" : "pending"}`}>{campaign.purchased_flag ? "Purchased" : "Pending purchase"}</span>
            <FiChevronDown className="summary-chevron" aria-hidden="true" />
          </summary>
          <div className="campaign-body">
            <div className="campaign-toolbar"><p>{campaign.total_orders} customer orders · {campaign.fulfilled_orders_count} fulfilled</p><div className="toolbar-actions"><label className={`purchase-flag-control ${campaign.purchased_flag ? "checked" : ""}`}><input type="checkbox" checked={campaign.purchased_flag} disabled={campaign.purchased_flag || busyCampaign === campaign.split_buy_id} onChange={() => setPurchaseConfirmation(campaign)} /><span><strong>{busyCampaign === campaign.split_buy_id ? "Updating…" : "Flag as purchased"}</strong><small>{campaign.purchased_flag ? "Campaign and orders synced" : "Mark campaign and every order"}</small></span></label></div></div>
            {campaign.orders.map((order) => <OrderAccordion order={order} key={order.order_id} />)}
          </div>
        </details>
      ))}

      <footer className="pagination">
        <div className="pagination-copy"><span>Showing</span><strong>{rangeStart}–{rangeEnd}</strong><span>of {pagination.total_items} campaigns</span></div>
        <nav className="pagination-controls" aria-label="Orders pagination">
          <button type="button" className="page-arrow" disabled={loading || pagination.current_page <= 1} onClick={() => loadOrders(pagination.current_page - 1)} aria-label="Previous page"><FiChevronLeft /></button>
          {paginationItems.map((item) => typeof item === "number" ? <button type="button" className={`page-number ${item === pagination.current_page ? "active" : ""}`} disabled={loading} onClick={() => loadOrders(item)} aria-current={item === pagination.current_page ? "page" : undefined} aria-label={`Page ${item}`} key={item}>{item}</button> : <span className="page-ellipsis" aria-hidden="true" key={item}>…</span>)}
          <button type="button" className="page-arrow" disabled={loading || pagination.current_page >= pagination.total_pages} onClick={() => loadOrders(pagination.current_page + 1)} aria-label="Next page"><FiChevronRight /></button>
        </nav>
      </footer>

      <Modal isOpen={Boolean(purchaseConfirmation)} toggle={() => !busyCampaign && setPurchaseConfirmation(null)} centered className="purchase-confirmation-modal" labelledBy="purchase-confirmation-title">
        {purchaseConfirmation && <>
          <ModalBody>
            <span className="confirmation-icon" aria-hidden="true"><FiAlertTriangle /></span>
            <div className="confirmation-copy"><span className="confirmation-eyebrow">Confirm purchase</span><h2 id="purchase-confirmation-title">Mark this Split-Buy as purchased?</h2><p>This will mark <strong>{purchaseConfirmation.product_title}</strong> as purchased and sync the purchased status across all {purchaseConfirmation.orders.length} customer order{purchaseConfirmation.orders.length === 1 ? "" : "s"}.</p><div className="confirmation-warning"><strong>This action affects the whole campaign.</strong><span>Confirm only after procurement has completed the supplier purchase.</span></div></div>
          </ModalBody>
          <ModalFooter><button type="button" className="confirmation-cancel" disabled={Boolean(busyCampaign)} onClick={() => setPurchaseConfirmation(null)}>Cancel</button><button type="button" className="confirmation-submit" disabled={Boolean(busyCampaign)} onClick={() => markPurchased(purchaseConfirmation)}>{busyCampaign ? "Marking as purchased…" : "Yes, mark as purchased"}</button></ModalFooter>
        </>}
      </Modal>
    </>
  );
};
